import {
  FrameLabel,
  MediaDetailGeometry,
  MediaDetailObservation,
  MediaDetailPrediction,
  MediaDetailPredictionLabel,
  MediaDetailSuggestion,
  PredictionReviewStatus,
  PredictionVisibility,
  ResolvedSuggestion,
  SuggestionVisibility,
} from "../common/ObjectTypes";
// Type-only: this module is a pure helper, and a value import would make it
// depend at runtime on a React component and everything that pulls in (i18n,
// spinners) purely to name a union type.
import type { SuggestionReviewState } from "./SuggestionCard";

/**
 * A render-ready overlay box composed from the resolved media-detail payload.
 * Structurally compatible with `BoundingBox` (so it can be passed straight to
 * `BoundingBoxOverlay`), plus the stable `predictionId` and `visibility` the
 * single-photo surface needs for selection and styling.
 */
export type DetailBox = {
  id: string; // String(prediction_id) — stable box identity for click/selection
  predictionId: number; // the detection prediction that owns the bbox
  // The prediction whose resolved primary label the box shows (the crop
  // classification override when present, else the detection). The popover reads
  // its labels + model provenance so the box and popover agree.
  sourcePredictionId: number;
  // AKL-777: EVERY label-bearing prediction on this box, so the popover can
  // show more than one model's candidates. `sourcePredictionId` above is still
  // the one whose label the box SHOWS; this is the full set. Falls back to just
  // that one when the API predates AKL-777.
  labelPredictionIds: number[];
  // AKL-703: the card this box belongs to, in the same key space as the
  // surface's `selectedCardKey` — `suggestionOwnerKey(s)` for a suggestion, or
  // an observation's id as a string. Lets box → card selection be a direct read
  // instead of matching each card against the box list.
  ownerKey: string;
  // Which kind of item `ownerKey` names. Stated explicitly rather than inferred
  // from the key's shape, because the per-box actions need it: on an
  // observation-owned box the committed observation — not the AI prediction — is
  // the source of the box's current species and of the aggregate whose count a
  // departing box decrements.
  ownerKind: "observation" | "suggestion";
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  // 0–1; omitted for an observation-owned box (AKL-712) — a committed
  // decision has no confidence of its own, and showing the AI's original
  // (possibly since-corrected) score next to the reviewer's resolved label
  // reads as if that score applied to the correction, which it never did.
  confidence: number | undefined;
  color: string;
  // The owning item's disposition. Widened to include the suggestion values so a
  // 'rejected' card's boxes can carry that through (AKL-703).
  visibility: PredictionVisibility | SuggestionVisibility;
};

/**
 * The minimal per-image shape `composeDetailBoxes`/`predictionsById` need.
 * `MediaDetailResponseDto` (the full-screen detail payload) satisfies this
 * structurally, as does the lighter per-asset `{predictions, suggestions,
 * observations}` triple the feed (`MediaAssetDto`) carries per AKL-731's DTO
 * unification — one compose function, fed by either shape, is what lets the
 * grid, film strip, and full-screen surfaces render identical boxes.
 */
export type DetailBoxSource = {
  predictions: MediaDetailPrediction[];
  suggestions: MediaDetailSuggestion[];
  observations: MediaDetailObservation[];
};

// Box color palette (§6.2.2 unactioned / accepted / rejected / edited).
// SENSIBLE DEFAULT — reconcile with the design doc §6.2.2 palette before merge
// (spec OQ-3, resolved). Read-only this slice, so only UNACTIONED renders in
// practice; the review-state branches light up once Slice 6 writes reviews.
const BOX_COLORS = {
  unactioned: "#E6C200", // neutral gold (matches the existing overlay default)
  accepted: "#22C55E", // green
  rejected: "#EF4444", // red
  edited: "#F59E0B", // amber
  outOfList: "#9CA3AF", // muted gray — resolved but outside the project list
} as const;

/**
 * Pick the primary label from a prediction's label list: prefer
 * `is_primary === true`, then `rank === 1`, then the first entry.
 * @param labels the prediction's labels
 * @returns the primary label, or null when there are none
 */
export const pickPrimaryLabel = (
  labels: MediaDetailPredictionLabel[],
): MediaDetailPredictionLabel | null => {
  if (!labels || labels.length === 0) return null;
  const byFlag = labels.find((l) => l.is_primary === true);
  if (byFlag) return byFlag;
  const byRank = labels.find((l) => l.rank === 1);
  if (byRank) return byRank;
  return labels[0];
};

/**
 * The effective box color, from the item's overlay disposition and its latest
 * review state. Disposition wins where the server already decided it:
 * `out_of_list` is always muted, `rejected` is always the danger red.
 * Otherwise the color reflects the review state, defaulting to the
 * unactioned neutral.
 *
 * AKL-731 (reject-reveal follow-up): a rejected cluster's box is hidden by
 * default everywhere (server omits `geometries`; the coordinates live in
 * `rejected_geometries` instead, see `composeDetailBoxes`), but the
 * full-screen panel reveals it in red on demand when its card is
 * clicked/hovered — so this disposition branch is back in active use, not
 * just a defensive fallback.
 * @param visibility the item's overlay disposition
 * @param latestReviewStatus the latest review status, or null when unactioned
 * @returns a hex color string
 */
export const boxColorFor = (
  visibility: PredictionVisibility | SuggestionVisibility,
  latestReviewStatus: PredictionReviewStatus | null,
): string => {
  if (visibility === "out_of_list") return BOX_COLORS.outOfList;
  if (visibility === "rejected") return BOX_COLORS.rejected;
  // AKL-717: an `unresolved` box is deliberately NOT special-cased here. It is
  // a pending detection like any other, so it takes the review-state path below
  // and lands on the unactioned neutral — the reviewer's cue that it still
  // needs a decision, exactly as for a labelled pending box.
  switch (latestReviewStatus) {
    case "accepted":
      return BOX_COLORS.accepted;
    case "rejected":
      return BOX_COLORS.rejected;
    case "edited":
      return BOX_COLORS.edited;
    default:
      return BOX_COLORS.unactioned;
  }
};

/**
 * Build a map of prediction_id → prediction for O(1) lookup (popover, per-box
 * confidence and raw-label fallback). Accepts either the full detail response
 * or just the `predictions` slice of the lighter per-asset `DetailBoxSource`
 * (AKL-731) — both carry `predictions` in the same shape.
 * @param source an object carrying `predictions` (detail response or per-asset triple)
 * @returns a Map keyed by prediction_id
 */
export const predictionsById = (
  source: Pick<DetailBoxSource, "predictions">,
): Map<number, MediaDetailPrediction> =>
  new Map(source.predictions.map((p) => [p.prediction_id, p]));

/**
 * Committed review state of a suggestion cluster, derived from its underlying
 * predictions' latest review: all-accepted → accepted, all-rejected → rejected,
 * none reviewed → null, otherwise edited/mixed (§6.2.2).
 *
 * Suggestion → prediction mapping is an explicit, reliable lookup
 * (`underlying_prediction_ids`), so this survived AKL-703's removal of the
 * observation reconstruction. It feeds both the card chips and the colour of the
 * card's overlay boxes, so it lives here alongside box composition rather than
 * being duplicated per call site.
 *
 * In practice it now returns `null` for every card it is called on: a pending
 * card's cluster contains only unreviewed predictions (AKL-700 excludes reviewed
 * ones), and a rejected card is identified by its server-assigned
 * `visibility: "rejected"` rather than by re-deriving the state here (AKL-703).
 * It is kept as a graceful-degradation guard — if the API ever hands back a
 * pending card containing a reviewed prediction, the chip and the box colour
 * reflect it instead of silently showing the cluster as unactioned. Do NOT use
 * it to detect rejection; read `visibility`.
 * @param suggestion the suggestion card
 * @param predictionMap prediction_id → prediction, from `predictionsById`
 * @returns the cluster's committed review state, or null when unreviewed
 */
export const deriveCardState = (
  suggestion: MediaDetailSuggestion,
  predictionMap: Map<number, MediaDetailPrediction>,
): SuggestionReviewState => {
  // Include unreviewed (null) statuses so a partially-reviewed cluster is NOT
  // shown as fully accepted/rejected: only an all-accepted / all-rejected
  // cluster gets that badge; any mix (incl. still-unreviewed members) is
  // "edited"; all-unreviewed is null (no badge).
  const statuses = suggestion.underlying_prediction_ids.map(
    (id) => predictionMap.get(id)?.latest_review?.status ?? null,
  );
  if (statuses.every((s) => s === null)) return null;
  if (statuses.every((s) => s === "accepted")) return "accepted";
  if (statuses.every((s) => s === "rejected")) return "rejected";
  return "edited";
};

/**
 * The ids of the boxes actually rendered for a given owner — the highlight set
 * for the card/row that carries `ownerKey`. An item's own geometry list is what
 * it *claims*; `boxes` is what survived composition, including the
 * de-duplication that hands a shared box to whichever item actually owns it (so
 * a box that split into a different observation stops highlighting under its
 * old owner). Deriving from ownership keeps card → box agreeing with the
 * box → card lookup in `handleBoxClick`, which reads the same `ownerKey`.
 * @param boxes the composed overlay boxes
 * @param ownerKey the owning item's key (aggregation key or observation id)
 * @returns the ids of that owner's rendered boxes
 */
export const boxIdsForOwner = (
  boxes: DetailBox[],
  ownerKey: string,
): string[] => boxes.filter((b) => b.ownerKey === ownerKey).map((b) => b.id);

/** Stable owner key for an observation's boxes — its id as a string, matching
 * the row key `ObservationList` uses for pointer selection. */
export const observationOwnerKey = (obs: MediaDetailObservation): string =>
  String(obs.id);

/**
 * Stable identity for a suggestion card: its visibility plus its aggregation
 * key.
 *
 * `aggregation_key` alone is **not** unique. The API builds it as
 * `taxon:<id>` for every card, and aggregates rejected clusters in a separate
 * pass from pending ones — so a species with both a pending and a rejected
 * cluster on one photo yields two cards carrying the identical key. Keyed on
 * that alone, selecting either card selects both, highlights both clusters'
 * boxes, and — for the assign-species action — would commit the cluster the
 * reviewer never ticked.
 *
 * Visibility is what separates them, so the pair is the identity used
 * everywhere the bare key was: card selection, box ownership, and the
 * multi-select set.
 *
 * It is stable for as long as a card keeps its disposition, which is **not**
 * forever — rejecting a pending card moves it to the rejected list and changes
 * its key. That is fine, and deliberately not worked around here: a card whose
 * disposition changed is a different thing, and an action changes what a card
 * is anyway (accepting or editing one turns it into an observation, with a
 * different owner key entirely). `applyDetail` drops a pointer-selected card
 * that the fresh detail no longer contains, which covers all three transitions
 * rather than only the one this key introduced.
 * @param suggestion the suggestion card
 * @returns a key unique across the pending, rejected and out-of-list lists
 */
export const suggestionOwnerKey = (suggestion: {
  aggregation_key: string;
  visibility: SuggestionVisibility;
}): string => `${suggestion.visibility}:${suggestion.aggregation_key}`;

/**
 * Whether a suggestion resolved to a species (AKL-717). Pending and rejected
 * suggestions render cards; an out-of-list one is resolved too but, since
 * AKL-717, image-only; an `unresolved` box has no species at all and is drawn
 * on the image alone. Narrowing with this is what lets card code read
 * `target_taxon_id` as a number without null checks.
 * @param suggestion any entry of the detail payload's `suggestions[]`
 * @returns true when the suggestion carries a species
 */
export const isResolvedSuggestion = (
  suggestion: MediaDetailSuggestion,
): suggestion is ResolvedSuggestion => suggestion.visibility !== "unresolved";

/**
 * Every owner key a fresh media detail actually contains — suggestion cards and
 * live observations.
 *
 * Used to drop a pointer-selected card that an action has made disappear. That
 * happens on every disposition change, not just one: accepting or editing a
 * card turns it into an observation with a different owner key, and rejecting
 * one moves it to the rejected list, which changes its `suggestionOwnerKey`.
 * Left alone, `selectedCardKey` keeps naming something gone, so its boxes stay
 * highlighted and the label toggle keeps rendering for a card nobody can see.
 *
 * Deleted observations are excluded — they render nothing, so a selection
 * pointing at one is exactly as stale as one pointing at a vanished card.
 * @param detail the fresh media detail response
 * @returns the owner keys currently renderable on this photo
 */
export const liveOwnerKeys = (detail: {
  suggestions?: MediaDetailSuggestion[] | null;
  observations?: MediaDetailObservation[] | null;
}): Set<string> =>
  new Set<string>([
    ...(detail.suggestions ?? []).map(suggestionOwnerKey),
    ...(detail.observations ?? [])
      .filter((o) => o.status !== "deleted")
      .map(observationOwnerKey),
  ]);

/**
 * The label to show on a box, given the item's resolved name and the prediction
 * the box takes its label from. When the taxon resolved to the "unknown"
 * category (the WI-Unknown fallback for a label with no taxon mapping) the raw
 * AI label is more informative to a reviewer than a generic "Unknown", so it
 * wins. The raw label is also the fallback when the item has no display name.
 */
const boxLabel = (
  displayName: string | null | undefined,
  observationType: string | null | undefined,
  labelPrediction: MediaDetailPrediction | undefined,
): string => {
  const rawLabel = labelPrediction
    ? pickPrimaryLabel(labelPrediction.labels)?.raw_label
    : undefined;
  if (observationType === "unknown") return rawLabel ?? displayName ?? "";
  return displayName ?? rawLabel ?? "";
};

/**
 * The text for a whole-photo label (AKL-1024): the label's resolved name, else
 * its raw AI label. A Wildlife Insights raw label is a taxon GUID, so showing
 * it raw put an unreadable ID on the photo while the suggestion card beside it
 * said "Leopard". As with `boxLabel`, a label that resolved to the "unknown"
 * category keeps its raw label, which tells a reviewer more than "Unknown".
 * @param label the prediction's primary label
 * @returns the text to show on the whole-photo label
 */
export const frameLabelText = (label: MediaDetailPredictionLabel): string => {
  if (label.observation_type === "unknown") return label.raw_label;
  return label.resolved_display_name || label.raw_label;
};

/**
 * Per-box confidence: the label-bearing prediction's object score, else its
 * primary label's score, else 0. Taken per prediction rather than from the
 * card's `confidence_max`, which is a cluster-wide figure and would show the
 * same number on every box of a multi-detection cluster.
 */
const boxConfidence = (
  labelPrediction: MediaDetailPrediction | undefined,
): number => {
  if (!labelPrediction) return 0;
  if (labelPrediction.object_score != null) return labelPrediction.object_score;
  return pickPrimaryLabel(labelPrediction.labels)?.score ?? 0;
};

/**
 * Compose the overlay boxes for a media asset from the geometry the API
 * attaches to each suggestion and each observation (AKL-703).
 *
 * AKL-731: this is now the single compose function shared by all three review
 * surfaces — full-screen review passes the full `MediaDetailResponseDto`, and
 * the grid/film strip pass the lighter per-asset `DetailBoxSource` triple the
 * feed carries (both endpoints return the same `MediaDetailPrediction`/
 * `MediaDetailSuggestion`/`MediaDetailObservation` DTO family). Whatever is
 * hidden or fallback-resolved server-side (exclusion, rejection) is therefore
 * identical across the grid, film strip, and full-screen for the same photo —
 * there is no client-side box-inclusion or label-fallback logic left anywhere
 * in the web app (the AKL-703-era `predictionComposer.tsx` override-walk this
 * replaced is deleted, not just supplemented).
 *
 * Committed observations are emitted first, then suggestions, and a box is
 * skipped when its owning prediction has already been claimed — so where both an
 * observation and a suggestion reference the same detection, the committed
 * decision wins and the box is never drawn twice. (Post-AKL-700 a reviewed
 * cluster no longer surfaces as a suggestion at all, so the overlap should not
 * arise; de-duplicating keeps the drawn-box count honest regardless.)
 *
 * Each box carries the label and colour of the item that owns it: an observation
 * always renders green (AKL-712 — accepted and edited are both resolved
 * decisions, not one lesser than the other), and a suggestion renders by its
 * own visibility and cluster review state — muted grey out-of-list, neutral
 * gold unactioned, danger red rejected. An `unresolved` box (AKL-717 — a
 * detection whose only label the project excluded) renders in the unactioned
 * neutral with no label and no confidence chip: the API already withholds it
 * for a box some card labels, and the `claimed` set below still covers the
 * observation-overlap case. A rejected suggestion's coordinates
 * come from `rejected_geometries` rather than `geometries` (which the API
 * always empties for it) — this function still produces a box for it, with
 * no label/confidence (see `push`), so it exists for `boxIdsForOwner` to find
 * when its card is selected. Whether it actually *renders* by default is a
 * render-layer decision, not this function's: `BoundingBoxOverlay` hides a
 * `visibility === "rejected"` box unless it's in the active highlight set
 * (full-screen's card click/hover), and `ReviewQueueStrip` filters it out
 * unconditionally (the film strip has no card-click UI to reveal it with).
 * `predictions` is still read, but only for per-box display detail (confidence
 * and the raw-label fallback) and by the popover — never to rebuild which box
 * belongs to which card.
 *
 * @param detail the media detail response, or the lighter per-asset triple (AKL-731)
 * @returns the overlay boxes to render (px coordinates)
 */
export const composeDetailBoxes = (
  detail: DetailBoxSource | null,
): DetailBox[] => {
  if (!detail) return [];

  const predictionMap = predictionsById(detail);
  const boxes: DetailBox[] = [];
  // Owning prediction ids already drawn, so one detection yields one box.
  const claimed = new Set<number>();

  const push = (
    geometry: MediaDetailGeometry,
    ownerKey: string,
    ownerKind: DetailBox["ownerKind"],
    displayName: string | null | undefined,
    observationType: string | null | undefined,
    color: string,
    visibility: PredictionVisibility | SuggestionVisibility,
  ) => {
    if (claimed.has(geometry.prediction_id)) return;
    claimed.add(geometry.prediction_id);
    const labelPrediction = predictionMap.get(geometry.label_prediction_id);
    boxes.push({
      id: String(geometry.prediction_id),
      predictionId: geometry.prediction_id,
      sourcePredictionId: geometry.label_prediction_id,
      labelPredictionIds:
        geometry.label_prediction_ids &&
        geometry.label_prediction_ids.length > 0
          ? geometry.label_prediction_ids
          : [geometry.label_prediction_id],
      ownerKey,
      ownerKind,
      x: geometry.bbox.x,
      y: geometry.bbox.y,
      width: geometry.bbox.width,
      height: geometry.bbox.height,
      // AKL-731: a revealed rejected box shows no label at all -- boxLabel's
      // own fallback chain would otherwise still surface the underlying
      // prediction's raw AI label, which is exactly the "no fallback" rule
      // rejection is supposed to enforce. AKL-717: an `unresolved` box is the
      // same case for a different reason — the only label it has is the one
      // the project excluded, and the raw-label fallback would leak it.
      label:
        visibility === "rejected" || visibility === "unresolved"
          ? ""
          : boxLabel(displayName, observationType, labelPrediction),
      // AKL-712: only a still-pending suggestion shows a confidence score —
      // once committed to an observation, the box shows the reviewer's
      // resolved label with no percentage. AKL-731: a revealed rejected box
      // has no label either, so a bare percentage with nothing to attach it
      // to would be confusing -- suppressed the same way. AKL-717: likewise
      // for an unresolved box, which shows no confidence anywhere: with no
      // species rows its popover names only the model, and that is deliberate.
      confidence:
        ownerKind === "observation" ||
        visibility === "rejected" ||
        visibility === "unresolved"
          ? undefined
          : boxConfidence(labelPrediction),
      color,
      visibility,
    });
  };

  for (const obs of detail.observations ?? []) {
    if (obs.status === "deleted") continue;
    // AKL-712: every committed observation reads as accepted (green) — an
    // edit is still a reviewer's resolved decision, not a lesser state that
    // needs its own color, so it no longer gets the amber "edited" treatment.
    for (const geometry of obs.geometries ?? []) {
      push(
        geometry,
        observationOwnerKey(obs),
        "observation",
        obs.display_name,
        obs.observation_type,
        BOX_COLORS.accepted,
        "visible",
      );
    }
  }

  for (const suggestion of detail.suggestions ?? []) {
    const color = boxColorFor(
      suggestion.visibility,
      deriveCardState(suggestion, predictionMap),
    );
    // A rejected cluster's coordinates live in `rejected_geometries`, not
    // `geometries` (which the API always empties for it) — the two are
    // mutually exclusive by construction, so concatenating is safe. This is
    // what makes the box exist in this function's output at all; whether it
    // actually renders by default is a render-layer concern (BoundingBoxOverlay
    // hides `visibility === "rejected"` unless its card is selected/hovered,
    // ReviewQueueStrip filters it out unconditionally — this composer's job
    // is only to make the data available, not to decide default visibility).
    for (const geometry of [
      ...(suggestion.geometries ?? []),
      ...(suggestion.rejected_geometries ?? []),
    ]) {
      push(
        geometry,
        suggestionOwnerKey(suggestion),
        "suggestion",
        suggestion.display_name,
        suggestion.observation_type,
        color,
        suggestion.visibility,
      );
    }
  }

  return boxes;
};

/**
 * Extract whole-media classification badges (AKL-645) from a media asset's
 * predictions — the `applies_to === "media"` entries render as frame-level
 * badges (top-right corner, via `BoundingBoxOverlay`'s `frameLabels` prop),
 * never as boxes. These predictions never own a bbox, so this is unaffected
 * by AKL-731's box-composition pipeline, but it does read `visibility`
 * directly: unlike `composeDetailBoxes` (which only ever sees geometries the
 * server has already resolved into suggestions/observations, so a suppressed
 * item's geometry is simply absent), this loop reads raw `predictions[]`, so
 * a `visibility: "suppressed"` prediction must be filtered out here
 * explicitly or its badge would render anyway. Was previously computed
 * alongside `Detection[]` by the now-deleted `predictionComposer.tsx`'s
 * `composePredictionRenderData` — this is its lightweight replacement so the
 * feed conversion (`ManageReviewPage.tsx`) can still populate `Image.frameLabels`.
 * Each badge reads the primary label's resolved name, falling back to its raw
 * label — see `frameLabelText` (AKL-1024).
 * @param predictions the media asset's predictions (unified `MediaDetailPrediction` shape)
 * @returns the frame-level badges to render
 */
export const composeFrameLabels = (
  predictions: MediaDetailPrediction[] | undefined,
): FrameLabel[] => {
  if (!predictions || predictions.length === 0) return [];
  const frameLabels: FrameLabel[] = [];
  for (const p of predictions) {
    if (p.applies_to !== "media") continue;
    // Unlike composeDetailBoxes (which only ever sees geometries the server
    // has already resolved into suggestions/observations, so a suppressed
    // item's geometry is simply absent), this loop reads raw predictions[]
    // directly -- so a server-suppressed whole-media classification must be
    // filtered here explicitly, or its badge would render anyway.
    if (p.visibility === "suppressed") continue;
    const primary = pickPrimaryLabel(p.labels);
    if (primary) {
      frameLabels.push({
        prediction_id: p.prediction_id,
        label: frameLabelText(primary),
        confidence: primary.score,
      });
    }
  }
  return frameLabels;
};
